import type { Locale } from '@/i18n'
import { LOCALE_INTL_TAGS } from '@/i18n/types'
import { type DateValue, parseDate } from '@ark-ui/solid/date-picker'

export const DEFAULT_TIME = '09:00'

/**
 * BCP-47 tag for the calendar grid and for a typed numeric date.
 *
 * Every app locale maps to a DD/MM/YYYY region on purpose: `en-US` would parse
 * a typed 03/04 as March 4 for some users and April 3 for others. The field
 * then displays the date with its month named (formatDisplay), so the reading
 * is never ambiguous. Do not swap these for the shared LOCALE_INTL_TAGS, whose
 * English tag is `en-US`.
 */
const CALENDAR_LOCALES: Record<Locale, string> = {
  en: 'en-GB',
  fr: 'fr-FR',
  es: 'es-MX',
  pt: 'pt-BR',
}

export function calendarLocale(locale: Locale): string {
  return CALENDAR_LOCALES[locale]
}

/**
 * Split a stored value into its date and wall-clock time halves.
 *
 * Never constructs a `Date`. Going through `Date` here would reinterpret the
 * wall-clock time in the viewer's timezone and shift the stored value by the
 * UTC offset.
 */
export function splitIso(value: string | undefined): { date: string; time: string } {
  if (!value) {
    return { date: '', time: '' }
  }
  const [date = '', rest = ''] = value.split('T')
  const time = rest.slice(0, 5)
  return { date, time }
}

export function joinIso(date: string, time: string, withTime: boolean): string {
  if (!date) {
    return ''
  }
  if (!withTime) {
    return date
  }
  return `${date}T${time || DEFAULT_TIME}`
}

export function toDateValues(date: string): DateValue[] {
  if (!date) {
    return []
  }
  try {
    return [parseDate(date)]
  } catch {
    return []
  }
}

export function toIsoDate(date: DateValue): string {
  const month = String(date.month).padStart(2, '0')
  const day = String(date.day).padStart(2, '0')
  return `${date.year}-${month}-${day}`
}

export function todayIso(): string {
  return new Date().toLocaleDateString('en-CA')
}

export function formatDisplay(date: DateValue, locale: Locale): string {
  return displayFormatter(locale).format(Date.UTC(date.year, date.month - 1, date.day))
}

export function parseDisplay(value: string, locale: Locale): DateValue | undefined {
  return parseNumeric(value) ?? parseNamedMonth(value, locale)
}

function displayFormatter(locale: Locale): Intl.DateTimeFormat {
  return new Intl.DateTimeFormat(LOCALE_INTL_TAGS[locale], {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

function parseNumeric(value: string): DateValue | undefined {
  const match = value.trim().match(/^(\d{1,2})[/\-. ]?(\d{1,2})[/\-. ]?(\d{4})$/)
  if (!match) {
    return undefined
  }
  const [, day, month, year] = match
  return validDate(Number(year), Number(month), Number(day))
}

function parseNamedMonth(value: string, locale: Locale): DateValue | undefined {
  const tokens = normalizeWord(value)
    .split(/[\s/,-]+/)
    .filter((token) => token !== '' && token !== 'de')
  if (tokens.length !== 3) {
    return undefined
  }
  const year = tokens.find((token) => /^\d{4}$/.test(token))
  const day = tokens.find((token) => /^\d{1,2}$/.test(token))
  const monthIndex = tokens
    .map((token) => monthNames(locale).indexOf(token))
    .find((index) => index >= 0)
  if (year === undefined || day === undefined || monthIndex === undefined) {
    return undefined
  }
  return validDate(Number(year), (monthIndex % 12) + 1, Number(day))
}

function monthNames(locale: Locale): string[] {
  const names = (style: 'short' | 'long') => {
    const formatter = new Intl.DateTimeFormat(LOCALE_INTL_TAGS[locale], {
      month: style,
      timeZone: 'UTC',
    })
    return Array.from({ length: 12 }, (_, month) =>
      normalizeWord(formatter.format(Date.UTC(2026, month, 15)))
    )
  }
  return [...names('short'), ...names('long')]
}

function normalizeWord(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replaceAll('.', '')
}

function validDate(year: number, month: number, day: number): DateValue | undefined {
  if (day < 1 || day > 31 || month < 1 || month > 12) {
    return undefined
  }
  const iso = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
  try {
    const date = parseDate(iso)
    return toIsoDate(date) === iso ? date : undefined
  } catch {
    return undefined
  }
}
